// Единое описание разделов приложения: путь, страница, заголовок, роли,
// место в меню. Отсюда собираются и меню (buildMenu), и маршруты (buildRoutes),
// поэтому они не могут разойтись — скрытый пункт меню больше не открывается
// по прямой ссылке.
//
// Роли живут на группах меню, а не на пунктах: раздел доступен тем, кто видит
// его хотя бы в одной группе. Для страниц без пункта меню роли задаются полем
// roles у самого раздела.
//
// Страницы грузятся лениво (React.lazy, см. app/buildRoutes.tsx): депонент
// не тянет экраны оператора.
//
// В этом файле нет ни JSX, ни импортов компонентов — иначе сборка меню
// затянула бы за собой RoleRoute и получился бы цикл импортов.

import type { ComponentType } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  BookOpen,
  Building2,
  Calculator,
  Calendar,
  CircleHelp,
  Ban,
  Banknote,
  CalendarClock,
  Coins,
  FileCheck,
  FileText,
  Globe,
  LayoutDashboard,
  LineChart,
  ListTree,
  Percent,
  Receipt,
  ReceiptText,
  Scale,
  List,
  Mail,
  MapPin,
  Package,
  RefreshCw,
  Settings,
  TrendingUp,
  Users,
  Webhook,
} from 'lucide-react';
import { CounterpartyType } from './counterparty';
import { ROLE_NAMES, type RoleName } from '../types/auth';
import type { MenuEntry, MenuItemBase } from '../types/menu';

const ADMIN = ROLE_NAMES.ADMIN;
const DEPOSITARY = ROLE_NAMES.AUTHORIZED_DEPOSITARY;
const REGISTRAR = ROLE_NAMES.REGISTRAR;
const OPERATOR = ROLE_NAMES.OPERATOR;
const CD_ACCOUNTANT = ROLE_NAMES.CD_ACCOUNTANT;
const USER_ADMIN = ROLE_NAMES.USER_ADMIN;

// ============= ТИПЫ =============

/** Группа меню. Без label пункты выводятся на верхнем уровне, без аккордеона. */
export interface MenuGroup {
  id: string;
  label?: string;
  roles?: RoleName[];
  forUnauthorized?: boolean;
}

/** Где показывать раздел в меню. */
export interface MenuPlacement {
  group: string;
  /** Заголовок именно в этой группе, если отличается от общего. */
  label?: string;
  dividerBefore?: boolean;
}

type PageLoader = () => Promise<{ default: ComponentType<never> }>;

export interface AppSection {
  path: string;
  /** Заголовок раздела: он же подпись в меню. */
  title?: string;
  /** Иконка в меню. Нет — подбирается по названию и префиксу пути. */
  icon?: LucideIcon;
  /** Пояснение под названием в развёрнутом меню. */
  subtitle?: string;
  load: PageLoader;
  /** Пропсы страницы (например, тип контрагента для общего списка). */
  props?: Record<string, unknown>;
  /** Места в меню. Пусто — страница есть, пункта меню нет. */
  menu?: MenuPlacement[];
  /** Роли для страницы без пункта меню. Обычно роли берутся из групп. */
  roles?: RoleName[];
  /**
   * Кому разрешена запись в этот раздел. Не задано — страница сама решает,
   * как раньше. Задано — те, кого нет в списке, видят раздел только на чтение:
   * общая таблица убирает колонку «Действия», а страница не рисует кнопки
   * создания, изменения и удаления (см. useWriteAccess).
   */
  writeRoles?: RoleName[];
  /** Доступна без авторизации — маршрут вне ProtectedLayout. */
  public?: boolean;
}

// ============= ПРАВО ЗАПИСИ В СПРАВОЧНИКИ =============
// Зеркало cdp/auth/roles.py на бэкенде. Если списки разойдутся, пользователь
// увидит кнопку, которая вернёт 403, поэтому правим их только вместе.
//
// Бухгалтер ЦД (cd_accountant) читает справочники, но не пишет: его нет ни в
// одном наборе, кроме своего WRITE_ACCOUNTING.

/** Валюты, курсы, страны, города, ценные бумаги и их типы/виды/формы. */
const WRITE_DICT_MARKET: RoleName[] = [OPERATOR];

/** Банки, план счетов, календари. */
const WRITE_DICT_COUNTERPARTY: RoleName[] = [ADMIN];

/**
 * Сами контрагенты и то, что ведётся из их карточки: реквизиты и лицензии.
 * Оператор ЦД ведёт их наравне с админом.
 */
const WRITE_COUNTERPARTIES: RoleName[] = [ADMIN, OPERATOR];

/** Клиенты контрагентов: депонент ведёт своих. У регистратора клиентов нет. */
const WRITE_COUNTERPARTY_CLIENTS: RoleName[] = [ADMIN, DEPOSITARY];

// Регистратора нет ни в одном наборе записи: справочники он только читает,
// пункты меню при этом остаются — чтение открыто.

/** Справочники бухгалтера ЦД: статьи доходов и расходов, типы коэффициентов. */
const WRITE_ACCOUNTING: RoleName[] = [CD_ACCOUNTANT];

/**
 * Выплата дохода по ЦБ: поступление от эмитента, начисление по депонентам.
 *
 * Сначала писала только бухгалтерия — это деньги и распределение их по чужим
 * счетам, а оператор ЦД раздел лишь видел. Решение Rus от 22.10.2026: доходами
 * управляет и оператор, он ведёт депозитарную часть выпуска. Состав обязан
 * совпадать с WRITE_INCOME_PAYOUT в backend/cdp/auth/roles.py: иначе кнопка
 * есть, а запрос отбивается.
 */
const WRITE_INCOME_PAYOUT: RoleName[] = [CD_ACCOUNTANT, OPERATOR];

/**
 * Кто видит проводки документа (окно «Проводки»). Повторяет роли эндпоинта
 * GET /documents/{id}/transactions (backend/cdp/documents/endpoints/documents.py):
 * оператор ЦД и бухгалтер ЦД, который ведёт доходы по ЦБ. Разойдутся — пункт
 * меню откроет окно с ошибкой 403.
 */
export const DOCUMENT_TRANSACTIONS_ROLES: RoleName[] = [OPERATOR, CD_ACCOUNTANT];

/**
 * Учётные записи пользователей: одобрение заявок, членства, вход по паролю,
 * блокировка. Раньше раздел был у оператора ЦД, то есть управление доступом
 * было смешано с депозитарной работой.
 */
const WRITE_USERS: RoleName[] = [USER_ADMIN];

/**
 * Почта контрагента «для доступа» (из договора): по ней контрагент подтверждает
 * своих сотрудников при самозаписи. Право отдельное от правки контрагентов:
 * карточку ведёт и оператор ЦД, а подменив этот адрес, он выдал бы доступ сам
 * себе. Повторяет WRITE_ACCESS_EMAILS в cdp/auth/roles.py.
 */
export const ACCESS_EMAILS_ROLES: RoleName[] = [ADMIN, USER_ADMIN];

/**
 * История изменений по карточке справочника (ценные бумаги, контрагенты).
 * Журнал целиком остаётся за администратором пользователей, а «кто поменял эту
 * запись» нужно тому, кто справочник ведёт. Повторяет HISTORY_ROLES в
 * cdp/audit/endpoints.py.
 */
export const REFERENCE_HISTORY_ROLES: RoleName[] = [ADMIN, OPERATOR, USER_ADMIN];

/**
 * Кто запускает задачи планировщика вручную (кнопка «Синхронизировать» в блоках
 * справочников на странице торговой сессии). Повторяет _ROLES в
 * backend/cdp/scheduler/endpoints.py (POST /jobs/schedules/{job}/run): разойдутся —
 * кнопка будет, а запуск отобьётся 403.
 */
export const JOB_RUN_ROLES: RoleName[] = [OPERATOR, ADMIN];

// ============= ГРУППЫ МЕНЮ =============
// Порядок групп = порядок разделов в меню.

export const MENU_GROUPS: MenuGroup[] = [
  { id: 'home-guest', forUnauthorized: true },
  { id: 'about-guest', label: 'О системе', forUnauthorized: true },
  { id: 'home' },
  { id: 'admin', label: 'Администрирование', roles: [ADMIN] },
  { id: 'users-audit', label: 'Пользователи и аудит', roles: [USER_ADMIN] },
  { id: 'dep-main', label: 'Основные', roles: [DEPOSITARY] },
  { id: 'dep-ts', label: 'Торговая система', roles: [DEPOSITARY] },
  { id: 'dep-reports', label: 'Отчёты', roles: [DEPOSITARY] },
  { id: 'op-main', label: 'Основные', roles: [OPERATOR] },
  { id: 'op-ts', label: 'Торговая система', roles: [OPERATOR] },
  { id: 'op-dict', label: 'Справочники', roles: [OPERATOR] },
  { id: 'op-reports', label: 'Отчёты', roles: [OPERATOR] },
  { id: 'accounting-main', label: 'Тарифы и начисления', roles: [CD_ACCOUNTANT] },
  // Своя группа, а не «Тарифы и начисления»: доход по ЦБ — не тарификация, и
  // оператору ЦД он нужен на чтение, а тарифы — нет.
  { id: 'income-payout', label: 'Доходы по ЦБ', roles: [CD_ACCOUNTANT, OPERATOR] },
  { id: 'accounting-dict', label: 'Справочники', roles: [CD_ACCOUNTANT] },
  { id: 'batches', label: 'Пакеты документов', roles: [REGISTRAR, OPERATOR] },
  { id: 'counterparty-dict', label: 'Справочники', roles: [ADMIN, REGISTRAR] },
  { id: 'about', label: 'О системе' },
];

// ============= РАЗДЕЛЫ =============
// Порядок разделов внутри группы = порядок в этом списке.

const counterpartyList = () => import('modules/dict/counterparty/CounterpartyList');

export const appSections: AppSection[] = [
  // — вход и активация (вне ProtectedLayout)
  { path: '/login', load: () => import('../../pages/LoginPage'), public: true },
  { path: '/register', load: () => import('../../pages/RegisterPage'), public: true },
  { path: '/login/esia/callback', load: () => import('../../pages/EsiaCallbackPage'), public: true },
  { path: '/not-activated', load: () => import('../../pages/NotActivatedPage'), public: true },
  { path: '/activate', load: () => import('../../pages/ActivatePage'), public: true },
  // Забытый пароль: запрос ссылки и смена по ней. Человек не вошёл в систему,
  // поэтому страницы публичные (docs/tasks/auth-password-ip-policy.md).
  {
    path: '/forgot-password',
    load: () => import('../../pages/ForgotPasswordPage'),
    public: true,
  },
  {
    path: '/reset-password',
    load: () => import('../../pages/ResetPasswordPage'),
    public: true,
  },
  // Решение контрагента по заявке на доступ: ссылки из письма. Страница только
  // показывает кнопку — действие делает POST по нажатию, иначе за человека
  // решил бы почтовый сканер (docs/tasks/user-registration-approval.md).
  { path: '/owner/confirm', load: () => import('../../pages/OwnerActionPage'), public: true },
  { path: '/owner/reject', load: () => import('../../pages/OwnerActionPage'), public: true },
  { path: '/owner/block', load: () => import('../../pages/OwnerActionPage'), public: true },

  // — общее
  {
    path: '/',
    icon: LayoutDashboard,
    subtitle: 'Обзор и мониторинг',
    title: 'Главная',
    load: () => import('../../pages/HomePage'),
    menu: [{ group: 'home-guest' }, { group: 'home' }],
  },
  { path: '/home', load: () => import('../../pages/HomePage'), icon: LayoutDashboard, subtitle: 'Обзор и мониторинг' },
  { path: '/messages', load: () => import('../../pages/MessagesPage') },
  {
    path: '/about/contacts',
    icon: Mail,
    subtitle: 'Связь с командой CDP',
    title: 'Контакты',
    load: () => import('../../pages/about/ContactsPage'),
    public: true,
    menu: [{ group: 'about-guest' }, { group: 'about' }],
  },
  {
    // Справка открыта любой роли: статьи не скрываются, чтобы депонент мог
    // прочитать, что делает оператор (решение 2 задания). Группа «О системе»
    // ролей не задаёт — значит, раздел видят все.
    //
    // public: справка нужна и до входа. Статья «Вход в систему, регистрация,
    // роли» описывает как раз страницу входа, и кнопка «Открыть на всю
    // страницу» в боковой панели оттуда не должна уводить на /login. Нового
    // это не раскрывает: тексты статей лежат в бандле, который отдаётся без
    // авторизации.
    path: '/help',
    icon: CircleHelp,
    subtitle: 'Инструкции по экранам',
    title: 'Справка',
    load: () => import('modules/help/pages/HelpPage'),
    public: true,
    menu: [{ group: 'about' }],
  },
  {
    // Та же страница по адресу статьи: /help/<статья>#<раздел>. Своего пункта
    // меню нет, роли не заданы, доступ тот же, что у /help.
    path: '/help/:slug',
    load: () => import('modules/help/pages/HelpPage'),
    public: true,
  },

  // Смена пароля: в меню не показывается (пункт живёт в меню профиля), но
  // экран нужен всем, у кого есть пароль. С просроченным паролем это
  // единственная доступная страница — остальное закрывает бэкенд.
  {
    path: '/password',
    title: 'Смена пароля',
    load: () => import('../../pages/ChangePasswordPage'),
  },

  // — пользователи и аудит
  // Путь остался /admin/users, чтобы не ломать сохранённые ссылки, но к группе
  // «Администрирование» раздел больше не относится: учётными записями ведает
  // отдельная роль useradmin, а не админ и не оператор ЦД.
  {
    path: '/admin/users',
    icon: Users,
    title: 'Пользователи',
    load: () => import('modules/admin/pages/UsersManagement'),
    menu: [{ group: 'users-audit' }],
    writeRoles: WRITE_USERS,
  },
  {
    path: '/audit',
    icon: ListTree,
    title: 'Журнал действий',
    subtitle: 'Входы, администрирование, отказы в доступе',
    load: () => import('modules/audit/pages/AuditJournal'),
    menu: [{ group: 'users-audit' }],
    // Пустой список намеренно: эндпоинтов записи у журнала нет вовсе, запись
    // закрыта триггерами в БД. Суперпользователь, как и везде, проходит любую
    // проверку прав — но писать и ему некуда.
    writeRoles: [],
  },

  // — администрирование
  {
    path: '/admin/settings',
    icon: Settings,
    title: 'Системные настройки',
    load: () => import('modules/admin/pages/ServerSettings'),
    menu: [{ group: 'admin', dividerBefore: true }],
  },
  {
    path: '/admin/reports',
    icon: FileText,
    title: 'Управление отчетами',
    load: () => import('modules/admin/pages/ReportsManagement'),
    menu: [{ group: 'admin' }],
  },
  {
    path: '/admin/document-types',
    icon: FileText,
    title: 'Виды документов',
    load: () => import('modules/documents/pages/DocumentTypeList'),
    menu: [{ group: 'admin', dividerBefore: true }],
  },
  {
    path: '/admin/document-status-transitions',
    icon: LineChart,
    title: 'Маршруты документов',
    load: () => import('modules/documents/pages/DocumentStatusTransitionList'),
    menu: [{ group: 'admin' }],
  },
  {
    path: '/admin/client-types',
    title: 'Типы клиентов',
    load: () => import('modules/dict/pages/ClientTypesManagement'),
    // разделитель перешёл сюда после того, как из меню убрали типы коэффициентов
    menu: [{ group: 'admin', dividerBefore: true }],
  },
  {
    path: '/admin/counterparty-sync',
    icon: RefreshCw,
    title: 'Синхронизация контрагентов',
    load: () =>
      import('modules/integrations/pages/CounterpartySyncPage').then((m) => ({
        default: m.CounterpartySyncPage,
      })),
    menu: [{ group: 'admin', dividerBefore: true }],
  },
  {
    path: '/admin/external-api',
    icon: Webhook,
    title: 'Внешние системы (M2M)',
    load: () => import('modules/external-api/pages/ServiceAccountsList'),
    menu: [{ group: 'admin', dividerBefore: true }],
  },
  {
    path: '/admin/external-api/audit-logs',
    title: 'Аудит-логи внешних систем',
    load: () => import('modules/external-api/pages/AuditLogsViewer'),
    menu: [{ group: 'admin' }],
  },
  {
    path: '/admin/external-api/roles-and-scopes',
    title: 'Роли и Scopes',
    load: () => import('modules/external-api/pages/RolesAndScopes'),
    menu: [{ group: 'admin' }],
  },
  {
    // подстраница раздела «Внешние системы», своего пункта меню нет
    path: '/admin/external-api/accounts/:accountId/keys',
    load: () => import('modules/external-api/pages/ApiKeysManagement'),
    roles: [ADMIN],
  },

  // — документы и операции
  {
    path: '/trading-day',
    title: 'Торговая сессия',
    load: () => import('modules/trading-day/TradingDayPage'),
    menu: [{ group: 'op-main' }],
  },
  {
    path: '/documents',
    icon: FileCheck,
    subtitle: 'Рабочий центр операций',
    title: 'Поручения',
    load: () => import('modules/documents/pages/DocumentList'),
    menu: [{ group: 'dep-main' }, { group: 'op-main' }],
  },
  {
    path: '/documents/income-requisites',
    title: 'Реквизиты по доходам',
    load: () => import('modules/documents/pages/IncomeRequisitesPage'),
    menu: [{ group: 'dep-main' }, { group: 'op-main' }],
  },
  {
    path: '/documents/cash-withdrawals',
    title: 'Банковские операции',
    load: () => import('modules/documents/pages/CashWithdrawalsPage'),
    menu: [{ group: 'dep-main' }, { group: 'op-main' }],
  },
  {
    path: '/deponent/clients',
    icon: Users,
    subtitle: 'Управление клиентами',
    title: 'Мои клиенты',
    load: () => import('modules/dict/counterparty-clients/CounterpartyClientsRoute'),
    menu: [{ group: 'dep-main' }],
    writeRoles: WRITE_COUNTERPARTY_CLIENTS,
  },
  {
    path: '/deponent/my-current-balances',
    icon: Archive,
    subtitle: 'Контроль позиций',
    title: 'Портфель',
    load: () =>
      import('modules/deponent/pages/balancesPage').then((m) => ({ default: m.BalancesPage })),
    menu: [{ group: 'dep-main' }],
  },
  {
    path: '/documents/limits-to-ts',
    icon: List,
    subtitle: 'Лимиты торговых систем',
    title: 'Лимиты для ТС',
    load: () => import('modules/documents/pages/DocLimitsToTSList'),
    menu: [{ group: 'op-main' }],
  },
  {
    path: '/documents/batch-summary',
    icon: Package,
    subtitle: 'Пакеты документов',
    title: 'Пакеты сводных документов',
    load: () => import('modules/documents/pages/DocOrdersBatchList'),
    // Один пункт на раздел: раньше он же дублировался в «Основных» оператора
    // под названием «Пакеты сводных документов».
    menu: [{ group: 'batches', label: 'Сводные документы' }],
  },

  // — торговая система
  {
    path: '/documents/trades',
    icon: TrendingUp,
    subtitle: 'Рыночные операции',
    title: 'Сделки',
    load: () => import('modules/documents/pages/QuikTradesList'),
    menu: [{ group: 'dep-ts' }, { group: 'op-ts' }],
  },
  {
    path: '/trading-system/orders',
    icon: List,
    subtitle: 'Активные заявки ТС',
    title: 'Активные заявки',
    load: () => import('modules/documents/pages/ActiveOrdersList'),
    menu: [{ group: 'dep-ts' }, { group: 'op-ts' }],
  },
  {
    path: '/trading-system/deponents',
    icon: Users,
    subtitle: 'Депоненты QUIK',
    title: 'Депоненты QUIK',
    load: () => import('modules/documents/pages/TplusZeroLimitDeponents'),
    menu: [{ group: 'op-ts' }],
  },

  // — отчёты
  {
    path: '/reports/balance-info',
    icon: FileText,
    subtitle: 'Сведения и отчётность',
    title: 'Сведения по остаткам',
    load: () => import('modules/reports/pages/BalanceInfoPage'),
    menu: [{ group: 'dep-reports' }, { group: 'op-reports' }],
  },
  {
    path: '/reports/balance-as-of',
    title: 'Остатки на дату и время',
    load: () => import('modules/reports/pages/BalanceAsOfPage'),
    menu: [{ group: 'dep-reports' }, { group: 'op-reports' }],
  },
  {
    path: '/reports/account-movements',
    title: 'Сведения по движениям',
    load: () => import('modules/reports/pages/AccountMovementsPage'),
    menu: [{ group: 'op-reports' }],
  },
  {
    path: '/reports/deponent-operations',
    title: 'Операции по счетам',
    load: () => import('modules/reports/pages/DeponentOperationsPage'),
    menu: [{ group: 'dep-reports' }, { group: 'op-reports' }],
  },
  {
    path: '/reports/bank-cash-movements',
    title: 'Движения и остатки ДС на счетах УБ',
    load: () => import('modules/reports/pages/BankCashMovementsPage'),
    menu: [{ group: 'op-reports' }],
  },

  // — доходы по ЦБ (купон, погашение, дивиденды)
  {
    path: '/income-payout/schedule',
    icon: CalendarClock,
    subtitle: 'Что эмитенты должны заплатить и что по этому сделано',
    title: 'График выплат',
    load: () => import('modules/documents/pages/IncomePayoutSchedulePage'),
    menu: [{ group: 'income-payout' }],
    writeRoles: WRITE_INCOME_PAYOUT,
  },
  {
    // карточка строки графика: пункта меню нет, роли те же, что у раздела
    // доходов (READ/WRITE_INCOME_PAYOUT в cdp/auth/roles.py)
    path: '/income-payout/schedule/:id',
    load: () => import('modules/documents/pages/IncomeRowCardPage'),
    roles: [CD_ACCOUNTANT, OPERATOR],
    writeRoles: WRITE_INCOME_PAYOUT,
  },
  {
    path: '/income-payout/receipts',
    icon: Coins,
    subtitle: 'Доход от эмитента через уполномоченный банк',
    title: 'Поступление дохода',
    load: () => import('modules/documents/pages/IncomeReceiptsPage'),
    menu: [{ group: 'income-payout' }],
    writeRoles: WRITE_INCOME_PAYOUT,
  },
  {
    path: '/income-payout/accruals',
    icon: Calculator,
    subtitle: 'Разнесение дохода по депонентам',
    title: 'Начисление дохода',
    load: () => import('modules/documents/pages/IncomeAccrualsPage'),
    menu: [{ group: 'income-payout' }],
    writeRoles: WRITE_INCOME_PAYOUT,
  },

  {
    path: '/income-payout/payouts',
    icon: Banknote,
    subtitle: 'Платежи депонентам по начисленному доходу',
    title: 'Выплата дохода',
    load: () => import('modules/documents/pages/IncomePayoutsPage'),
    menu: [{ group: 'income-payout' }],
    writeRoles: WRITE_INCOME_PAYOUT,
  },
  {
    path: '/income-payout/holds',
    icon: Ban,
    subtitle: 'Депоненты, которым выплата приостановлена',
    title: 'Приостановка выплат',
    load: () => import('modules/documents/pages/IncomePayoutHoldsPage'),
    menu: [{ group: 'income-payout' }],
    writeRoles: WRITE_INCOME_PAYOUT,
  },

  // — тарифы бухгалтера ЦД
  {
    path: '/tariffs',
    icon: Receipt,
    title: 'Тарифы',
    load: () => import('modules/dict/pages/TariffList'),
    menu: [{ group: 'accounting-main' }],
    writeRoles: WRITE_ACCOUNTING,
  },
  {
    path: '/accruals',
    icon: ReceiptText,
    title: 'Начисления по тарифам',
    subtitle: 'Журнал начислений депонентам и очередь тарификации',
    load: () => import('modules/dict/pages/AccrualsPage'),
    menu: [{ group: 'accounting-main' }],
    writeRoles: WRITE_ACCOUNTING,
  },
  {
    path: '/third-party-invoices',
    icon: Scale,
    title: 'Счета сторонних организаций',
    subtitle: 'Входящие счета банка и реестродержателя, сверка с начислениями',
    load: () => import('modules/dict/pages/ThirdPartyInvoicesPage'),
    menu: [{ group: 'accounting-main' }],
    writeRoles: WRITE_ACCOUNTING,
  },
  {
    // карточка редакции: пункта меню нет, поэтому роли задаём явно — иначе
    // sectionRoles вернёт undefined и раздел откроется любому авторизованному
    path: '/tariffs/:id',
    load: () => import('modules/dict/pages/TariffCard'),
    roles: [CD_ACCOUNTANT],
    writeRoles: WRITE_ACCOUNTING,
  },

  // — справочники бухгалтера ЦД
  // Идут первыми, чтобы в его «Справочниках» свои разделы были сверху,
  // а чужие (только на чтение) — ниже, за разделителями.
  {
    path: '/dict/services',
    icon: ListTree,
    title: 'Услуги',
    load: () => import('modules/dict/pages/ServiceList'),
    menu: [{ group: 'accounting-dict' }],
    writeRoles: WRITE_ACCOUNTING,
  },
  {
    path: '/dict/tax-rates',
    icon: Percent,
    title: 'Ставки налогов',
    subtitle: 'НДС и налог с продаж с периодом действия',
    load: () => import('modules/dict/pages/TaxRateList'),
    menu: [{ group: 'accounting-dict' }],
    writeRoles: WRITE_ACCOUNTING,
  },
  {
    path: '/dict/counterparty-coefficient-types',
    title: 'Типы коэффициентов контрагентов',
    load: () => import('modules/dict/pages/CounterpartyCoefficientTypeList'),
    menu: [{ group: 'accounting-dict' }],
    writeRoles: WRITE_ACCOUNTING,
  },

  // — справочники оператора ЦД
  // Бухгалтеру ЦД они нужны на чтение, кроме стран и городов.
  {
    path: '/dict/currencies',
    icon: Coins,
    title: 'Валюты',
    load: () => import('modules/dict/pages/CurrencyList'),
    menu: [{ group: 'op-dict' }, { group: 'accounting-dict', dividerBefore: true }],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/currency-rates',
    title: 'Курсы валют',
    load: () => import('modules/dict/pages/CurrencyRatesPage'),
    menu: [{ group: 'op-dict' }, { group: 'accounting-dict' }],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/countries',
    icon: Globe,
    title: 'Страны',
    load: () => import('modules/dict/pages/CountryList'),
    menu: [{ group: 'op-dict' }],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/cities',
    icon: MapPin,
    title: 'Города',
    load: () => import('modules/dict/pages/CityList'),
    menu: [{ group: 'op-dict' }],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/securities',
    icon: LineChart,
    title: 'Ценные бумаги',
    load: () => import('modules/dict/pages/SecurityList'),
    menu: [
      { group: 'op-dict', dividerBefore: true },
      { group: 'accounting-dict', dividerBefore: true },
    ],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/security-types',
    title: 'Типы ЦБ',
    load: () => import('modules/dict/pages/SecurityTypeList'),
    menu: [{ group: 'op-dict' }, { group: 'accounting-dict' }],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/security-kinds',
    title: 'Виды ЦБ',
    load: () => import('modules/dict/pages/SecurityKindList'),
    menu: [{ group: 'op-dict' }, { group: 'accounting-dict' }],
    writeRoles: WRITE_DICT_MARKET,
  },
  {
    path: '/dict/security-forms',
    title: 'Формы ЦБ',
    load: () => import('modules/dict/pages/SecurityFormList'),
    menu: [{ group: 'op-dict' }, { group: 'accounting-dict' }],
    writeRoles: WRITE_DICT_MARKET,
  },

  // — справочники контрагентов и счетов
  // Бухгалтеру ЦД — на чтение, кроме календарей.
  {
    path: '/dict/authorized-depositaries',
    title: 'Авторизованные депоненты',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.AUTHORIZED_DEPOSITARY },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict', dividerBefore: true },
      { group: 'accounting-dict', dividerBefore: true },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/owners',
    title: 'Владельцы ЦБ',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.OWNER_OF_SECURITIES },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict' },
      { group: 'accounting-dict' },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/issuers',
    title: 'Эмитенты',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.ISSUER },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict' },
      { group: 'accounting-dict' },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/registrars',
    title: 'Регистраторы',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.REGISTRAR },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict' },
      { group: 'accounting-dict' },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/depositaries',
    title: 'Депозитарии',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.DEPOSITARY },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict' },
      { group: 'accounting-dict' },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/exchanges',
    title: 'Биржи',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.EXCHANGE },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict' },
      { group: 'accounting-dict' },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/authorized-banks',
    title: 'Уполномоченные банки',
    load: counterpartyList,
    props: { counterpartyType: CounterpartyType.AUTHORIZED_BANK },
    menu: [
      { group: 'counterparty-dict' },
      { group: 'op-dict' },
      { group: 'accounting-dict' },
    ],
    writeRoles: WRITE_COUNTERPARTIES,
  },
  {
    path: '/dict/banks',
    icon: Building2,
    title: 'Банки',
    load: () => import('modules/dict/pages/BankList'),
    menu: [
      { group: 'counterparty-dict', dividerBefore: true },
      { group: 'accounting-dict', dividerBefore: true },
    ],
    writeRoles: WRITE_DICT_COUNTERPARTY,
  },
  {
    path: '/dict/chart-of-accounts',
    icon: BookOpen,
    title: 'План счетов',
    load: () => import('modules/dict/pages/ChartOfAccountsList'),
    menu: [{ group: 'counterparty-dict', dividerBefore: true }, { group: 'accounting-dict' }],
    writeRoles: WRITE_DICT_COUNTERPARTY,
  },
  {
    path: '/calendars',
    icon: Calendar,
    title: 'Календари',
    load: () => import('modules/calendar/pages/CalendarsPage').then((m) => ({
      default: m.CalendarsPage,
    })),
    menu: [{ group: 'counterparty-dict', dividerBefore: true }],
    writeRoles: WRITE_DICT_COUNTERPARTY,
  },
];

// ============= СБОРКА МЕНЮ =============

const groupById = new Map(MENU_GROUPS.map((group) => [group.id, group]));

/** Роли раздела: явные или объединение ролей групп, где он показывается. */
export function sectionRoles(section: AppSection): RoleName[] | undefined {
  if (section.roles) return section.roles;
  if (!section.menu?.length) return undefined;

  const roles = new Set<RoleName>();
  for (const placement of section.menu) {
    const group = groupById.get(placement.group);
    // группа без ролей — раздел открыт любому авторизованному
    if (!group?.roles) return undefined;
    group.roles.forEach((role) => roles.add(role));
  }
  return Array.from(roles);
}

export function buildMenu(sections: AppSection[] = appSections): MenuEntry[] {
  return MENU_GROUPS.flatMap((group) => {
    const entries: MenuEntry[] = [];

    for (const section of sections) {
      const placement = section.menu?.find((p) => p.group === group.id);
      if (!placement || !section.title) continue;
      if (placement.dividerBefore) entries.push({ type: 'divider' });

      const item: MenuItemBase = { label: placement.label ?? section.title, link: section.path };
      // группа без заголовка не рисует аккордеон: её пункты идут на верхний
      // уровень и сами несут признак «для неавторизованных»
      if (!group.label && group.forUnauthorized) item.forUnauthorized = true;
      entries.push(item);
    }

    if (!entries.length) return [];
    if (!group.label) return entries;

    const groupItem: MenuItemBase = { label: group.label, roles: group.roles, children: entries };
    if (group.forUnauthorized) groupItem.forUnauthorized = true;
    return [groupItem];
  });
}

const sectionByPath = new Map(appSections.map((section) => [section.path, section]));

/** Раздел по точному пути — для иконки и подписи в меню. */
export function findSection(path?: string): AppSection | undefined {
  return path ? sectionByPath.get(path) : undefined;
}
